import { useEffect, useState } from "react";
import { deleteField, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db, WORKSPACE_ID } from "./firebase";

/**
 * The WhatsApp messages behind the Today cards, and the one place they are
 * written down (ADR-034).
 *
 * Asked for directly: a card for somebody who donated, a WhatsApp button that
 * "already writes the message", and "מרכז הודעות והגדרות שאני שם עורך את כל
 * הטקסטים ... אם אני לא אוהב את מה שאתה כתבת". So the engine says *which* message
 * a card needs (`messageKey`) and supplies the facts (`messageVars`); the words
 * are here as defaults and in `message_templates/{workspace}` as the owner's
 * edits. Changing a sentence never needs a deploy.
 *
 * ## The template language, all of it
 *
 * - `{שם}` and friends are replaced by the card's facts.
 * - `[ … ]` is an optional piece: shown only if every `{…}` inside it has a
 *   value. "תודה על התרומה[ של {סכום}]" reads right with an amount and without
 *   one, which a plain placeholder cannot do in Hebrew.
 *
 * Nothing else. A template language people have to learn is a template nobody
 * edits.
 */

export interface MessageDef {
  key: string;
  /** What the owner sees in the message centre. */
  title: string;
  /** When the message is offered, in a sentence. */
  when: string;
  /** The facts this message can use, beyond {שם} and {חתימה}. */
  vars: string[];
  text: string;
}

export const MESSAGE_DEFS: MessageDef[] = [
  {
    key: "donation_unthanked",
    title: "תודה על תרומה",
    when: "כשמישהו תרם דרך האתר או Grow, ועוד לא דיברתם איתו",
    vars: ["סכום", "מטרה"],
    text: "שלום {שם}, תודה רבה על התרומה[ של {סכום}][ ל{מטרה}]! 🙏\nזה עוזר לנו להמשיך את הפעילות בשכונה. תזכו למצוות!\n{חתימה}",
  },
  {
    key: "donation_standing_order",
    title: "תודה על הצטרפות להוראת קבע",
    when: "בחיוב הראשון של הוראת קבע חדשה",
    vars: ["סכום"],
    text: "שלום {שם}, תודה ענקית שהצטרפתם להוראת קבע[ של {סכום}]! 🙏\nהתמיכה הקבועה שלכם היא מה שמחזיק את בית חב״ד. תזכו לכל טוב!\n{חתימה}",
  },
  {
    key: "partner_thanks",
    title: "תודה על הצטרפות לברכת השנה",
    when: "כשמישהו נרשם לשותפות השנתית בברכת השנה",
    vars: ["מסלול"],
    text: "שלום {שם}, תודה רבה שהצטרפתם לשותפות השנתית בברכת השנה[ ({מסלול})]! 🙏\nבזכות השותפים שלנו בית חב״ד פתוח לכולם כל השנה. נתפלל עליכם ועל כל המשפחה, שתזכו לשנה טובה ומתוקה!\n{חתימה}",
  },
  {
    key: "kapparot_thanks",
    title: "תודה על פדיון כפרות",
    when: "כשמישהו שילם פדיון כפרות באתר",
    vars: ["סכום"],
    text: "שלום {שם}, תודה רבה על פדיון הכפרות[ ({סכום})]! תזכו לגמר חתימה טובה ולשנה טובה ומתוקה. 🙏\n{חתימה}",
  },
  {
    key: "birthday_upcoming",
    title: "יום הולדת",
    when: "שבוע לפני יום ההולדת",
    vars: ["מתי"],
    text: "מזל טוב {שם}! 🎉\nמאחלים לך שנה של בריאות, שמחה והצלחה. יום הולדת הוא יום מיוחד לקבל החלטה טובה אחת לשנה החדשה.\n{חתימה}",
  },
  {
    key: "birthday_child",
    title: "יום הולדת לילד/ה",
    when: "שבוע לפני יום ההולדת של ילד",
    vars: ["מתי"],
    text: "מזל טוב ליום ההולדת של {שם}! 🎂\nמאחלים שנה מלאה בשמחה, בריאות ונחת.\n{חתימה}",
  },
  {
    key: "yahrzeit_upcoming",
    title: "יארצייט",
    when: "שבוע לפני היארצייט",
    vars: ["נפטר", "קרבה", "תאריך", "מתי"],
    text: "שלום {שם}, רציתי להזכיר שהיארצייט[ של {נפטר}] חל[ {מתי}][ ({תאריך})].\nנשמח לארגן מניין, קדיש ולימוד משניות לעילוי הנשמה. תגידו לי מה מתאים לכם.\n{חתימה}",
  },
  {
    key: "enquiry_unanswered",
    title: "מענה לפנייה מהאתר",
    when: "כשמישהו השאיר פנייה באתר ומחכה לתשובה",
    vars: ["נושא"],
    text: "שלום {שם}, קיבלנו את הפנייה שלך מהאתר. תודה שפנית!\nמתי נוח לדבר?\n{חתימה}",
  },
  {
    key: "follow_up_due",
    title: "חזרה למשימה שקבעתם",
    when: "כשמגיע התאריך של משימה עם איש קשר",
    vars: ["נושא"],
    text: "שלום {שם}, רציתי לחזור אליך[ בעניין {נושא}]. מתי נוח לדבר?\n{חתימה}",
  },
  {
    key: "registration_unwelcomed",
    title: "תודה על הרשמה",
    when: "כשמישהו נרשם לאירוע ועוד לא חזרתם אליו",
    vars: ["אירוע"],
    text: "שלום {שם}, תודה שנרשמתם[ ל{אירוע}]! שמחים שתצטרפו אלינו.\nאם יש שאלות, אני כאן.\n{חתימה}",
  },
  {
    key: "event_confirm",
    title: "אישור הגעה לאירוע",
    when: "עד 3 ימים לפני אירוע שנרשמו אליו",
    vars: ["אירוע", "מתי"],
    text: "שלום {שם}, רק מוודאים שאתם מגיעים[ ל{אירוע}][ {מתי}]? נשמח לראות אתכם!\n{חתימה}",
  },
  {
    key: "event_regular_reminder",
    title: "תזכורת לשיעור",
    when: "ביום של שיעור, למי שהגיע אליו בעבר",
    vars: ["אירוע"],
    text: "שלום {שם}, רק להזכיר שהיום[ {אירוע}]. נשמח לראות אותך!\n{חתימה}",
  },
  {
    key: "significant_no_contact",
    title: "מזמן לא דיברנו",
    when: "לתורם קבוע שלא דיברתם איתו 3 חודשים",
    vars: [],
    text: "שלום {שם}, מה שלומך? מזמן לא דיברנו ורציתי לשמוע מה נשמע. 🙂\n{חתימה}",
  },
  {
    key: "service_request_open",
    title: "בקשה שממתינה",
    when: "כשמישהו ביקש שירות (מזוזה, ביקור) ועוד לא טיפלתם",
    vars: ["בקשה"],
    text: "שלום {שם}, קיבלנו את הבקשה שלך[ ({בקשה})]. מתי נוח לתאם?\n{חתימה}",
  },
  {
    key: "signature_mendi",
    title: "חתימה של מענדי",
    when: "מופיעה במקום {חתימה} בהודעות שמענדי שולח",
    vars: [],
    text: "מנחם מענדל סויסא, בית חב״ד הפארק",
  },
  {
    key: "signature_shira",
    title: "חתימה של שירה",
    when: "מופיעה במקום {חתימה} בהודעות ששירה שולחת",
    vars: [],
    text: "שירה, בית חב״ד הפארק",
  },
];

const DEF_BY_KEY = new Map(MESSAGE_DEFS.map((d) => [d.key, d]));

/**
 * Which message a card needs.
 *
 * Cards written before `messageKey` existed have only a rule id, and the keys
 * were chosen to equal the rule ids for exactly this reason; the one exception
 * is a child's birthday, which the rule marks in its reason codes.
 */
export function messageKeyFor(rec: {
  messageKey?: string | null;
  ruleId?: string;
  reasonCodes?: string[];
}): string | null {
  if (rec.messageKey) return rec.messageKey;
  if (rec.ruleId === "birthday_upcoming" && rec.reasonCodes?.includes("child")) return "birthday_child";
  return rec.ruleId && DEF_BY_KEY.has(rec.ruleId) ? rec.ruleId : null;
}

/**
 * The name to open a message with.
 *
 * The first word of the name, because "שלום ישראל" is how a person is addressed
 * and "שלום ישראל ישראלי" is how a bill is. A family card keeps its whole name —
 * "שלום משפחת כהן" and not "שלום משפחת".
 */
export function addressName(fullName: string | null | undefined): string {
  const name = (fullName ?? "").trim();
  if (name === "") return "";
  if (/^(משפחת|בית)\s/.test(name)) return name;
  return name.split(/\s+/)[0] ?? name;
}

/** Fills a template. `[ … ]` pieces with any empty fact inside are dropped. */
export function renderTemplate(text: string, vars: Record<string, string>): string {
  const value = (k: string) => (vars[k] ?? "").trim();
  const optional = text.replace(/\[([^\]]*)\]/g, (_m, inner: string) => {
    const names = [...inner.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]!);
    return names.every((n) => value(n) !== "") ? inner : "";
  });
  return optional
    .replace(/\{([^}]+)\}/g, (_m, k: string) => value(k))
    .replace(/[ \t]+\n/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/** The owner's wording where they changed it, the default everywhere else. */
export function templateText(templates: Record<string, string>, key: string): string {
  return templates[key] ?? DEF_BY_KEY.get(key)?.text ?? "";
}

/** The finished message for one card, or null when there is no message for it. */
export function messageFor(
  templates: Record<string, string>,
  rec: {
    messageKey?: string | null;
    ruleId?: string;
    reasonCodes?: string[];
    messageVars?: Record<string, string>;
    personName?: string;
  },
  senderSlug: string | null,
): string | null {
  const key = messageKeyFor(rec);
  if (!key) return null;
  const signature = senderSlug ? templateText(templates, `signature_${senderSlug}`) : "";
  return renderTemplate(templateText(templates, key), {
    ...(rec.messageVars ?? {}),
    שם: addressName(rec.personName),
    חתימה: signature,
  });
}

/** The owner's edits, live. Empty until loaded, which renders the defaults. */
export function useMessageTemplates(): { templates: Record<string, string>; loaded: boolean } {
  const [state, set] = useState<{ templates: Record<string, string>; loaded: boolean }>({
    templates: {},
    loaded: false,
  });
  useEffect(
    () =>
      onSnapshot(
        doc(db, "message_templates", WORKSPACE_ID),
        (snap) =>
          set({
            templates: (snap.exists() ? (snap.get("templates") as Record<string, string>) : null) ?? {},
            loaded: true,
          }),
        // A failed read falls back to the defaults rather than to no message: a
        // card whose WhatsApp button stops working because of a permissions
        // error is worse than one with the stock wording.
        () => set({ templates: {}, loaded: true }),
      ),
    [],
  );
  return state;
}

/** Saves one message, or with `text === null` returns it to the default. */
export async function saveTemplate(key: string, text: string | null, uid: string): Promise<void> {
  await setDoc(
    doc(db, "message_templates", WORKSPACE_ID),
    {
      workspaceId: WORKSPACE_ID,
      templates: { [key]: text === null ? deleteField() : text },
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}
